import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { jakarta } from '@/lib/fonts';
import { cn } from '@/lib/utils';

interface Slide {
  id: number;
  title: string;
  description: string;
  image: string;
}

interface Props {
  slides: Slide[];
  locale: string;
}

export const HeroSlideshow = ({ slides, locale }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!slides || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides]);

  if (!slides || slides.length === 0) return null;

  const currentSlide = slides[currentIndex];
  const isVideo = currentSlide.image?.toLowerCase().endsWith('.mp4');

  return (
    <div className="relative h-screen w-full overflow-hidden bg-black">
      <AnimatePresence>
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: 'easeInOut' }}
          className="absolute inset-0"
        >
          {currentSlide.image && (
            isVideo ? (
              <video
                src={`${process.env.NEXT_PUBLIC_STRAPI_URL || ''}${currentSlide.image}`}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <Image
                src={`${process.env.NEXT_PUBLIC_STRAPI_URL || ''}${currentSlide.image}`}
                alt={currentSlide.title || 'Slide image'}
                fill
                priority
                className="object-cover"
              />
            )
          )}
          <div className="absolute inset-0 bg-black/40" />
          
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className={cn(
                jakarta.className,
                'text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white max-w-5xl tracking-tight leading-[1.1]'
              )}
            >
              {currentSlide.title}
            </motion.h1>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-6 text-lg md:text-2xl text-white/90 max-w-3xl font-medium leading-relaxed"
            >
              {currentSlide.description}
            </motion.p>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Progress Indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex space-x-3 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-1.5 transition-all duration-500 rounded-full ${
              index === currentIndex ? 'w-8 bg-[#c02029]' : 'w-2 bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};